"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { track } from "@vercel/analytics";
import { SiteNavigation as SharedSiteNavigation, type MegaMenuItem } from "@dj/site-navigation";
import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";
import { Link, usePathname } from "@/i18n/navigation";
import { normalizeLocale } from "@/i18n/routing";
import { isDirectoryHandoffMenuId, marketplaceMenuHref } from "@/lib/marketplaceMenuHref";
import { reactWebAppHref, reactWebAuthHref, requestReactWebLogout } from "@/lib/reactWebAuthUrl";
import { resolveViewerChromeSessionFromCookieHeader, type ViewerChromeSession } from "@/lib/viewerSession";

export default function SiteNavigation({ viewerSession }: { viewerSession: ViewerChromeSession }) {
  const t = useTranslations("navigation");
  const locale = normalizeLocale(useLocale());
  const pathname = usePathname();
  const [chromeSession, setChromeSession] = useState(viewerSession);
  useEffect(() => {
    const hydrateChromeSession = () => setChromeSession(resolveViewerChromeSessionFromCookieHeader(document.cookie));
    const hydrateVisibleChromeSession = () => { if (document.visibilityState === "visible") hydrateChromeSession(); };
    hydrateChromeSession();
    window.addEventListener("focus", hydrateChromeSession);
    document.addEventListener("visibilitychange", hydrateVisibleChromeSession);
    return () => {
      window.removeEventListener("focus", hydrateChromeSession);
      document.removeEventListener("visibilitychange", hydrateVisibleChromeSession);
    };
  }, []);
  const marketplaceMenus = (t.raw("marketplaceMenus") as MegaMenuItem[]).map((item) =>
    item.id === "find-missions" ? {
      ...item,
      href: reactWebAppHref(locale, "skyrules"),
      label: t("skyRules.label"),
      subtitle: t("skyRules.subtitle"),
      badge: t("skyRules.badge"),
      openInNewTab: true,
    } : item,
  );
  const localeKeys = { en: "switchToEnglish", es: "switchToSpanish", "pt-BR": "switchToPortugueseBrazil" };
  return <SharedSiteNavigation
    viewerSession={chromeSession} locale={locale} pathname={pathname} translate={t}
    navigation={{ megaMenus: t.raw("megaMenus") as MegaMenuItem[], marketplaceMenus }}
    LinkComponent={Link}
    brand={<Image src="/brand/dronejob-header-wordmark.png" alt={t("brandAlt")} width={600} height={126} sizes="(min-width: 1536px) 220px, (min-width: 1280px) 130px, 210px" className="transition-all duration-300" priority />}
    languageSwitcher={<LanguageSwitcher ariaLabel={t("language")} getLocaleLabel={(option) => t(localeKeys[option])} locale={locale} pathname={pathname} placement="navigation" />}
    appLinksOpenInNewTab
    appLinks={{ dashboard: reactWebAppHref(locale, "dashboard"), "my-profile": reactWebAppHref(locale, "my-profile"), settings: reactWebAppHref(locale, "settings"), login: reactWebAuthHref(locale, "login"), signup: reactWebAuthHref(locale, "signup") }}
    onLogout={() => requestReactWebLogout(locale)}
    marketplaceHref={(item) => item.id === "find-missions" ? item.href : isDirectoryHandoffMenuId(item.id) ? marketplaceMenuHref(locale, item) : null}
    onNavigation={track}
  />;
}
